import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DoorOpen } from "lucide-react";
import { toast } from "sonner";

import { useAnswerKnock, useKnocks } from "../hooks/useOffice";
import { useOfficeRealtime } from "../realtime/OfficeRealtimeProvider";
import { Avatar } from "./Avatar";

interface IncomingKnock {
  id: string;
  from_user_id: string;
  from_user_name: string;
  from_user_avatar_url?: string;
  intent: "chat" | "call";
  reason?: string;
}

/**
 * The interrupt prompt shown to somebody in focus mode.
 *
 * One card at a time, newest first. Accepting hands off to WeCo Chat or WeCo
 * Call — office decides whether the door opens, not what happens in the room.
 */
export function KnockPrompts() {
  const { t } = useTranslation();
  const { subscribe } = useOfficeRealtime();
  const { data } = useKnocks();
  const answer = useAnswerKnock();
  const navigate = useNavigate();
  const [live, setLive] = useState<IncomingKnock[]>([]);

  useEffect(
    () =>
      subscribe((frame) => {
        if (frame.type !== "knock.received") return;
        window.dispatchEvent(new CustomEvent("weco:office:attention"));
        const knock = frame.payload as unknown as IncomingKnock;
        setLive((prev) =>
          prev.some((k) => k.id === knock.id) ? prev : [knock, ...prev],
        );
      }),
    [subscribe],
  );

  // Anything already pending when the tab opened, merged with what arrives live.
  const pending: IncomingKnock[] = [
    ...live,
    ...(data?.results ?? [])
      .filter((k) => !live.some((l) => l.id === k.id))
      .map((k) => ({
        id: k.id,
        from_user_id: k.from_user.id,
        from_user_name: k.from_user.name,
        from_user_avatar_url: k.from_user.avatar_url,
        intent: k.intent,
        reason: k.reason,
      })),
  ];

  const current = pending[0];
  if (!current) return null;

  const respond = (accepted: boolean) => {
    answer.mutate(
      { id: current.id, accepted },
      {
        onSuccess: (knock) => {
          setLive((prev) => prev.filter((k) => k.id !== current.id));
          if (!accepted) return;
          // A call opens inside HomeWork, the way walking into a meeting room
          // does; the knock id is what tells the module which room to join.
          // Chat is a different service and still links out to it.
          navigate(
            knock.intent === "chat"
              ? `/chat?dm=${encodeURIComponent(current.from_user_id)}`
              : `/homework?call=${encodeURIComponent(current.id)}`,
          );
        },
        onError: (error: unknown) => {
          setLive((prev) => prev.filter((k) => k.id !== current.id));
          toast.error(error instanceof Error ? error.message : t("homework.knock.expired"));
        },
      },
    );
  };

  return (
    <div
      role="alertdialog"
      aria-labelledby="knock-title"
      className="fixed bottom-6 end-6 z-[120] w-80 rounded-2xl border border-border bg-background p-4 shadow-2xl"
    >
      <div className="mb-3 flex items-center gap-3">
        <Avatar
          name={current.from_user_name}
          id={current.from_user_id}
          url={current.from_user_avatar_url}
          size={40}
        />
        <div className="min-w-0">
          <p id="knock-title" className="flex items-center gap-1.5 text-[14px] font-semibold">
            <DoorOpen className="size-4 text-primary" />
            {t("homework.knock.knocking", { name: current.from_user_name })}
          </p>
          <p className="text-[12px] text-muted-foreground">
            {current.intent === "call" ? t("homework.knock.wantsCall") : t("homework.knock.wantsChat")}
          </p>
        </div>
      </div>
      {current.reason && (
        <p className="mb-3 rounded-lg bg-muted/50 px-3 py-2 text-[12px]">{current.reason}</p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={answer.isPending}
          onClick={() => respond(true)}
          className="flex-1 rounded-lg bg-primary px-3 py-2 text-[13px] font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {t("homework.knock.letIn")}
        </button>
        <button
          type="button"
          disabled={answer.isPending}
          onClick={() => respond(false)}
          className="flex-1 rounded-lg border border-border px-3 py-2 text-[13px] font-semibold transition-colors hover:bg-muted disabled:opacity-50"
        >
          {t("homework.knock.notNow")}
        </button>
      </div>
      {pending.length > 1 && (
        <p className="mt-2 text-center text-[11px] text-muted-foreground">
          {t("homework.knock.moreWaiting", { count: pending.length - 1 })}
        </p>
      )}
    </div>
  );
}
